<template>
  <div class="w-full h-full">
    <Card>
      <CardHeader>
        <CardTitle>Perks catalogue</CardTitle>
        <CardDescription>View pre-integrated perks available across our network</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="products"
          :loading="loading"
          :error="error"
          @retry="getProductCatalogue(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by perk or retailer name"
          :search="productSearchInput"
          @update:search="productSearchInput = $event"
          @search="getProductCatalogue(0, serverOptions.rowsPerPage)"
          @clear-search="onClearSearch()"
          :filter-groups="productFilterGroups"
          :filters="productFilters"
          @filter-change="onProductFilterChange"
        >
          <template #toolbar-actions>
            <Select v-model="orderBy" @update:model-value="onSortChange">
              <SelectTrigger size="sm" class="w-40" aria-label="Order by">
                <SelectValue placeholder="Order by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in orderByList" :key="o.id" :value="o.id">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </template>
          <template #item-product="product">
            <div class="flex items-center gap-2.5">
              <img
                v-if="product.retailerLogo"
                class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white"
                :src="product.retailerLogo"
                alt=""
              />
              <div class="min-w-0">
                <p class="text-sm font-medium truncate" :title="product.productName">{{ product.productName }}</p>
                <p class="text-xs text-muted-foreground mt-1 truncate" :title="product.retailerName">{{ product.retailerName }}</p>
              </div>
            </div>
          </template>

          <!-- Type and category moved out of the product cell into their own column. -->
          <template #item-details="product">
            <div class="flex flex-wrap gap-1 items-center">
              <Badge variant="secondary">{{ formatType(product.type) }}</Badge>
              <Badge v-if="product.category" variant="secondary">{{ product.category }}</Badge>
              <Badge v-if="product.country" variant="outline">{{ product.country }}</Badge>
            </div>
          </template>

          <template #item-minimumOrder="product">
            <span v-if="product.isRanged" class="text-sm tabular-nums whitespace-nowrap">{{ product.lowerBound }} - {{ product.upperbound }}</span>
            <span v-else class="text-sm tabular-nums whitespace-nowrap">{{ product.minimum }}</span>
          </template>

          <template #item-value="product">
            <span v-if="product.isValueSetOnOrder" class="text-sm text-muted-foreground whitespace-nowrap">Set on order</span>
            <span v-else class="text-sm tabular-nums whitespace-nowrap">£{{ product.publisherPerUnitPrice.toFixed(2) }}</span>
          </template>

          <!-- Suppliers come through as short codes (ELLO, …); show the trading name and
               link out to them rather than surfacing the raw code. -->
          <template #item-supplier="product">
            <a
              v-if="product.supplier && supplierUrl(product.supplier)"
              :href="supplierUrl(product.supplier)"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-primary underline-offset-2 hover:underline whitespace-nowrap"
            >{{ supplierName(product.supplier) }}</a>
            <span v-else-if="product.supplier" class="text-sm whitespace-nowrap">{{ supplierName(product.supplier) }}</span>
            <span v-else class="text-muted-foreground">—</span>
          </template>

          <template #item-action="product">
            <div class="flex justify-end items-center gap-1">
              <Button variant="outline" size="sm" @click="$router.push(`/product-catalogue/${product.id}`)">View</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@boogi/ui'
import { DataTable } from '@boogi/ui'

export default {
  name: 'product-catalogue',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
  },
  props: {},

  data() {
    return {
      productSearchInput: '',
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      downloading: false,

      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      // Perk suppliers, keyed by the code the API returns. Unknown codes fall back to the
      // code itself so nothing disappears if a new supplier is added server-side.
      perkSuppliers: {
        ELLO: { name: 'Ello Group', url: 'https://www.ellogroup.co.uk/' },
        ELLOGROUP: { name: 'Ello Group', url: 'https://www.ellogroup.co.uk/' },
        VALUEDYNAMX: { name: 'Valuedynamx', url: 'https://www.valuedynamx.com/' },
        ACTION: { name: 'Action Solutions', url: '' },
        ACTIONSOLUTIONS: { name: 'Action Solutions', url: '' },
      },

      headersTable1: [
        { text: 'Perk', value: 'product', width: 400 },
        { text: 'Details', value: 'details' },
        { text: 'Supplier', value: 'supplier' },
        { text: 'Minimum order', value: 'minimumOrder' },
        { text: 'Per unit price', value: 'value' },
        { text: 'Currency', value: 'currency' },
        { text: 'Action', value: 'action' },
      ],
      productTypes: [
        { id: 'VOUCHER', text: 'Voucher' },
        { id: 'GIFTCARD', text: 'Giftcard' },
      ],
      categories: [],
      productType: [],
      productCategory: [],
      countryOptions: [],
      selectedCountry: [],
      orderBy: 'MOSTPOPULAR',
      orderByList: [
        { id: 'MOSTPOPULAR', text: 'Most popular' },
        { id: 'ATOZ', text: 'A to Z' },
        { id: 'ZTOA', text: 'Z to A' },
        { id: 'LATEST', text: 'Newest' },
      ],
      products: [],
    }
  },
  created() {
    this.getCategories()
    this.getCountries()
    this.getProductCatalogue(0, 25)
  },
  methods: {
    supplierEntry(code) {
      return this.perkSuppliers[String(code).toUpperCase().replace(/[^A-Z]/g, '')] || null
    },
    supplierName(code) {
      const entry = this.supplierEntry(code)
      return (entry && entry.name) || code
    },
    supplierUrl(code) {
      const entry = this.supplierEntry(code)
      return (entry && entry.url) || ''
    },

    formatType(type) {
      if (type === 'VOUCHER') return 'Voucher'
      if (type === 'GIFTCARD') return 'Giftcard'
      return type
    },

    onClearSearch() {
      this.productSearchInput = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getProductCatalogue(0, this.serverOptions.rowsPerPage)
    },

    onProductFilterChange(updated) {
      const typeIds = (updated && updated.productType) || []
      this.productType = this.productTypes.filter((o) => typeIds.includes(o.id))
      this.productCategory = (updated && updated.productCategory) || []
      this.selectedCountry = (updated && updated.country) || []
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getProductCatalogue(0, this.serverOptions.rowsPerPage)
    },

    onSortChange() {
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getProductCatalogue(0, this.serverOptions.rowsPerPage)
    },

    async getCountries() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue/countries`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `${token}` },
          }
        )
        if (response.ok) {
          this.countryOptions = await response.json()
        }
      } catch (error) {
        return
      }
    },

    async getCategories() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue/categories`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )
        if (response.ok) {
          this.categories = await response.json()
        }
      } catch (error) {
        return
      }
    },

    async getProductCatalogue(page, size) {
      this.loading = true
      this.error = false
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const params = new URLSearchParams()
        params.append('page', page)
        params.append('size', size)
        if (this.productSearchInput) params.append('search', this.productSearchInput)
        if (this.productType.length > 0) {
          params.append('type', this.productType.map((t) => t.id).join(','))
        }
        if (this.productCategory.length > 0) {
          params.append('category', this.productCategory.join(','))
        }
        if (this.selectedCountry.length > 0) {
          params.append('country', this.selectedCountry.join(','))
        }
        params.append('sort', this.orderBy)

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.products = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    productFilterGroups() {
      const groups = [
        {
          key: 'productType',
          label: 'Type',
          options: this.productTypes.map((o) => ({ value: o.id, label: o.text })),
        },
      ]
      if (this.categories.length > 0) {
        groups.push({
          key: 'productCategory',
          label: 'Category',
          options: this.categories.map((c) => ({ value: c, label: c })),
        })
      }
      if (this.countryOptions.length > 0) {
        groups.push({
          key: 'country',
          label: 'Country',
          options: this.countryOptions.map((c) => ({ value: c, label: c })),
        })
      }
      return groups
    },
    productFilters() {
      return {
        productType: this.productType.map((o) => o.id),
        productCategory: this.productCategory,
        country: this.selectedCountry,
      }
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getProductCatalogue(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
